// src/App.jsx
import { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Routes, Route, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { useUser }  from './contexts/UserContext.jsx';
import { useOrder } from './contexts/OrderContext';
import { api } from './utilities/api';
import { startSessionTimer, clearSessionTimer } from './utilities/utilities';
import {
  capturePaymentCapability,
  clearPaymentAction,
  getPaymentAction,
  storePaymentAction,
} from './utilities/paymentAction';
import SessionTimeoutModal from './components/SessionTimeoutModal';
import ConfirmCancel from './components/ConfirmCancel';
import Spinner from './components/Spinner';
import Terms from './components/Terms.jsx';
import Privacy from './components/Privacy';
import Layout from './components/Layout';
import Header from './components/Header';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import PasskeyEnrollmentPrompt from './components/PasskeyEnrollmentPrompt';
import LandingPage from './pages/LandingPage';
import RegistrationWorkflow from './pages/RegistrationWorkflow';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import PricingPage from './pages/PricingPage';
import Profile from './pages/Profile';
import PaymentsPage from './pages/PaymentsPage';
import SchedulePage from './pages/SchedulePage';
import FullListPage from './pages/FullListPage';
import RefillsPage from './pages/RefillsPage';
import Encounter from './pages/Encounter';
import { useNavigateWithContext } from './hooks/useNavigateWithContext.jsx';

function ProtectedRoutes() {
  const { isLoggedIn, loadingSession } = useUser();
  const location = useLocation();

  if (loadingSession) {
    return <Spinner size="lg" overlay={true}>Retrieving user information</Spinner>;
  }

  return isLoggedIn ? <Outlet /> : <Navigate to="/login" replace state={{ from: location }} />;
}

function PaymentAccessRoute() {
  const { isLoggedIn, loadingSession } = useUser();
  const { actionPaymentComplete } = useOrder();
  const action = getPaymentAction();

  if (action || actionPaymentComplete || isLoggedIn) return <PaymentsPage />;
  if (loadingSession) {
    return <Spinner size="lg" overlay={true}>Retrieving user information</Spinner>;
  }

  return <Navigate to="/" replace />;
}

function App() {
  const {logout, refreshUserInfo, loadingSession, checkSession, isLoggedIn} = useUser();
  const { setCurrentOrder } = useOrder();
  const navigateWithContext = useNavigateWithContext();

  const [showSessionTimeoutModal, setShowSessionTimeoutModal] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const [capturedPaymentCapability] = useState(capturePaymentCapability);
  const [isActionExchangeLoading, setIsActionExchangeLoading] = useState(
    capturedPaymentCapability !== null
  );
  // One-shot guard: the capability exchange must fire at most once per page
  // load, regardless of `navigate` identity churn on pathname changes. The
  // capability itself is deliberately multi-use server-side (48h TTL) - this
  // only stops this SPA instance from re-exchanging it.
  const exchangeAttemptedRef = useRef(false);

  // setup timers to warn user if jwt is about to expire
  window.startSessionTimer = startSessionTimer;
  window.clearSessionTimer = clearSessionTimer;
  
  // clean up and terminate the jwt and user
  window.logoutUser = (silent = false) => {
    setShowSessionTimeoutModal(false);
    logout(true);
    if (!silent) {
      navigate('/');
    }
  }

  // setup global handle to show the timeout modal when the timeout occurs regardless what page
  window.showSessionTimeoutModal = (show) => setShowSessionTimeoutModal(show);

  const handleConfirmCancel = () => {
    setShowConfirmCancel(false);
  };

  useEffect(() => {
    if (capturedPaymentCapability === null) return undefined;
    if (exchangeAttemptedRef.current) return undefined;
    exchangeAttemptedRef.current = true;

    let cancelled = false;
    const exchangeCapability = async () => {
      if (!capturedPaymentCapability) {
        clearPaymentAction();
        toast.error('Unable to open this payment link.');
        navigate('/', { replace: true });
        setIsActionExchangeLoading(false);
        return;
      }

      setIsActionExchangeLoading(true);
      const response = await api.exchangePaymentActionCapability({
        capability: capturedPaymentCapability,
      });
      if (cancelled) return;

      const action = storePaymentAction(response);
      if (!action) {
        toast.error(response?.error || 'Unable to open this payment link.');
        navigate('/', { replace: true });
        setIsActionExchangeLoading(false);
        return;
      }

      navigate('/payment', { replace: true });
      setIsActionExchangeLoading(false);
    };

    exchangeCapability();
    return () => {
      cancelled = true;
    };
  }, [capturedPaymentCapability, navigate]);

  // Process deep links on initial render and when location changes
  useEffect(() => {
    // Function to process URL parameters
    const processUrlParams = async () => {
      const searchParams = new URLSearchParams(location.search);
      const fragmentParams = new URLSearchParams(location.hash.replace(/^#/, ''));

      const step = fragmentParams.get('step') || searchParams.get('step');
      const orderId = searchParams.get('order');
      const appointmentId = searchParams.get('appointmentid');
      const capability = fragmentParams.get('capability') || searchParams.get('capability');

      try {
        // Registration flow
        if (step === 'otp') {
          if (!capability) {
            toast.error('This verification link is incomplete or expired.');
            navigate('/register', { replace: true });
            return;
          }

          // Move the opaque capability out of the URL immediately so it is not
          // retained in browser history or sent as a referrer.
          navigate('/register', {
            replace: true,
            state: { step: 'otp', capability },
          });
          return;
        }
        
        // Payment deep link
        if (step === 'payment') {
          if (capturedPaymentCapability !== null) return;

          if (searchParams.has('userid') || searchParams.has('otp') || searchParams.has('token')) {
            toast.error('Unable to open this payment link.');
            navigate('/', { replace: true });
            return;
          }

          if (!orderId) {
            toast.error('Unable to open this payment link.');
            navigate('/', { replace: true });
            return;
          }

          const safeSearch = `?${new URLSearchParams({
            step: 'payment',
            order: orderId,
          }).toString()}`;

          // Legacy links carried both factors in the URL. Strip them before
          // doing any work and preserve only the resource identifier.
          if (location.pathname !== '/' || location.search !== safeSearch) {
            navigate(`/${safeSearch}`, { replace: true });
            return;
          }

          if (loadingSession) return;
          if (!isLoggedIn) {
            toast.error('Unable to open this payment link.');
            navigate('/', { replace: true });
            return;
          }

          try {
            // First, ensure user data is fully loaded
            await refreshUserInfo();
            
            // Then get order details
            const orderDetails = await api.getOrderDetail({ orderid: orderId });
            
            if(!orderDetails.success || !orderDetails.data) {
              toast.error('Failed to retrieve order details');
              return;
            }

            // Set the order data synchronously and ensure it's persisted to localStorage
            flushSync(() => {
              setCurrentOrder(orderDetails.data);
              // Also save to localStorage directly as a backup
              localStorage.setItem('redimdwm_current_order', JSON.stringify(orderDetails.data));
            });

            // Add a small delay to ensure state updates are processed
            setTimeout(() => {
              navigate("/payment", {replace: true});
            }, 100);
          } catch {
            toast.error('Failed to prepare payment page. Please try again.');
          }

          //navigateWithContext(orderId, '/payment', 'order');
          return;
        }
        
        // Appointment/encounter deep link
        if (step === 'encounter') {
          if (searchParams.has('userid') || searchParams.has('otp') || searchParams.has('token')) {
            toast.error('Unable to open this appointment link.');
            navigate('/', { replace: true });
            return;
          }

          if (!appointmentId) {
            toast.error('Missing appointment information');
            navigate('/', { replace: true });
            return;
          }

          const safeSearch = `?${new URLSearchParams({
            step: 'encounter',
            appointmentid: appointmentId,
          }).toString()}`;

          if (location.pathname !== '/' || location.search !== safeSearch) {
            navigate(`/${safeSearch}`, { replace: true });
            return;
          }

          if (loadingSession) return;
          if (!isLoggedIn) {
            toast.error('Unable to open this appointment link.');
            navigate('/', { replace: true });
            return;
          }

          await refreshUserInfo();
          await navigateWithContext(appointmentId, '/encounter', 'appointment');
          return;
        }

        // Fail closed for unknown legacy links rather than retaining identity or
        // authentication material in the address bar.
        if (searchParams.has('userid') || searchParams.has('otp') || searchParams.has('token')) {
          navigate(location.pathname, { replace: true });
        }
      } catch {
        toast.error('Unable to open this link.');
        navigate('/', { replace: true });
      }
    };
    
    // Process parameters if there are any
    if (location.search || location.hash) {
      processUrlParams();
    }
  }, [capturedPaymentCapability, location, isLoggedIn, loadingSession]); // React to location and session changes

  return (
    <>
      {loadingSession && (<Spinner size='lg' overlay={true}>Retrieving user information</Spinner>)}
      {isActionExchangeLoading && (<Spinner size='lg' overlay={true}>Preparing payment</Spinner>)}
	      {showSessionTimeoutModal && (
	        <SessionTimeoutModal
	          visible={showSessionTimeoutModal}
	          onContinue={checkSession}
	          onLogout={() => {
	            logout(true);
	            navigate('/');
	          }}
	        />
	      )}

      {showConfirmCancel && (
        <ConfirmCancel
          visible={showConfirmCancel}
          onConfirm={handleConfirmCancel}
          onCancel={() => setShowConfirmCancel(false)}
        />
      )}
      <Toaster position='top-center' containerStyle={{ top: '4.5em', minHeight: '5em' }} />
      <PwaInstallPrompt />
      <PasskeyEnrollmentPrompt />
      <div className="min-h-screen flex flex-col">
        <Header />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route element={<Layout />}>
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/register" element={<RegistrationWorkflow />} />
            <Route path="/payment" element={<PaymentAccessRoute />} />
            <Route element={<ProtectedRoutes />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/profile" element={<Profile   />} />
              <Route path="/orders" element={<FullListPage listType="orders" />}/>
              <Route path="/appointments" element={<FullListPage listType="appointments" />}/>
              <Route path="/encounter" element={<Encounter   />} />
              <Route path="/schedule" element={<SchedulePage />} />
              <Route path="/refills" element={<RefillsPage   />} />
            </Route>
          </Route>
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </div>
    </>
  );
}

export default App;
